import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ProjectService } from 'src/app/Services/project.service';
import { ContactPhoneCaptureService } from '../contact-phone-capture.service';
import { JourneyContactPhoneStripComponent } from '../journey-contact-phone-strip/journey-contact-phone-strip.component';
import { helpRequestPhone } from '../team-size.util';

export interface JourneyAssistedSetupDialogData {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  workspaceName: string;
  teamSizeBand: string;
}

@Component({
  selector: 'app-journey-assisted-setup-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, JourneyContactPhoneStripComponent],
  templateUrl: './journey-assisted-setup-dialog.component.html',
  styleUrls: ['./journey-assisted-setup-dialog.component.css'],
})
export class JourneyAssistedSetupDialogComponent {
  submitting = false;
  error = '';
  draftPhone = '';

  @ViewChild('assistedPhoneStrip') phoneStrip?: JourneyContactPhoneStripComponent;

  constructor(
    private dialogRef: MatDialogRef<JourneyAssistedSetupDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: JourneyAssistedSetupDialogData,
    private projectService: ProjectService,
    private contactPhoneCapture: ContactPhoneCaptureService,
  ) {
    this.draftPhone = helpRequestPhone(this.data?.contactPhone);
  }

  get needsPhoneCapture(): boolean {
    return !helpRequestPhone(this.data?.contactPhone);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  onPhoneDraft(phone: string): void {
    this.draftPhone = String(phone || '').trim();
    if (this.error && helpRequestPhone(this.draftPhone)) {
      this.error = '';
    }
  }

  onPhoneSaved(phone: string): void {
    this.draftPhone = String(phone || '').trim();
    if (this.data) {
      this.data.contactPhone = this.draftPhone;
    }
  }

  confirm(): void {
    if (this.submitting) {
      return;
    }
    const name = String(this.data?.contactName || '').trim();
    const email = String(this.data?.contactEmail || '').trim();
    if (!name || !email) {
      this.error = 'We need your name and email on this account to request a setup call.';
      return;
    }

    const existing = helpRequestPhone(this.data?.contactPhone);
    if (existing) {
      this.submitHelpRequest(existing);
      return;
    }

    this.phoneStrip?.flush();
    const draft = helpRequestPhone(this.draftPhone || this.phoneStrip?.composedDraft);
    if (!draft || !this.contactPhoneCapture.isValidPhone(draft)) {
      this.error = 'Add a mobile number so we can reach you for the setup call.';
      this.phoneStrip?.focusInput();
      return;
    }

    this.submitting = true;
    this.error = '';
    this.contactPhoneCapture.savePhone(draft, 'assisted_setup').subscribe({
      next: () => {
        if (this.data) {
          this.data.contactPhone = draft;
        }
        this.draftPhone = draft;
        this.submitHelpRequest(draft);
      },
      error: () => {
        this.submitting = false;
        this.error = 'Could not save the mobile number. Try again.';
      },
    });
  }

  private submitHelpRequest(phone: string): void {
    const workspace = String(this.data?.workspaceName || '').trim() || '(unnamed)';
    const band = String(this.data?.teamSizeBand || '').trim() || '(not selected)';
    this.submitting = true;
    this.error = '';
    this.projectService.createHelpRequest({
      request_type: 'demo',
      source: 'activation_setup',
      source_page_url: typeof window !== 'undefined' ? window.location.pathname : '/setup/understand',
      source_context: {
        route: typeof window !== 'undefined' ? window.location.pathname : '/setup/understand',
        feature: 'assisted_setup',
        product_area: 'activation',
        step: 'plan_viewed',
      },
      subject: 'Assisted setup',
      description: `Assisted setup from activation. Workspace: ${workspace}. Team size: ${band}.`,
      contact_name: String(this.data?.contactName || '').trim(),
      contact_email: String(this.data?.contactEmail || '').trim(),
      contact_phone: phone,
    }).subscribe({
      next: (res: any) => {
        this.submitting = false;
        if (res && res.success === false) {
          this.error = res.error || 'Could not send the request. Try again.';
          return;
        }
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.submitting = false;
        this.error = err?.error?.error || 'Could not send the request. Try again.';
      },
    });
  }
}
